/**
 * Pure utility functions for attendance save logic.
 * Extracted from useAttendanceSave for testability.
 */

export interface AttendanceEntry {
  student_id: string;
  status: string;
  note: string | null;
  late_minutes?: number | null;
  arrival_time?: string | null;
}

export interface ConcurrentCourseData {
  courses?: Array<{
    lesson_id: string;
    course_name: string;
    is_cancelled?: boolean;
    students: Array<{ id: string; name: string }>;
  }>;
}

/**
 * Extract student IDs enrolled in active (non-cancelled) concurrent courses.
 * These students should NOT receive carry-over attendance from a double lesson.
 */
export function getCourseStudentIds(
  concurrentData: ConcurrentCourseData | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  if (!concurrentData?.courses) return ids;

  const activeCourses = concurrentData.courses.filter(
    (c) => !c.is_cancelled,
  );
  for (const course of activeCourses) {
    for (const s of course.students) {
      ids.add(s.id);
    }
  }
  return ids;
}

/**
 * Build the consecutive lesson attendance array from the current lesson's
 * attendance. Transformations applied per-student:
 *
 * 1. Exclude students enrolled in concurrent courses (courseStudentIds).
 * 2. Anderswo: students who were absent because they were elsewhere in the
 *    school (library, etc) come back next period — flip to `present`.
 * 3. late → present (they're no longer late to the 2nd period).
 */
export function buildConsecutiveAttendance(
  attendanceArray: AttendanceEntry[],
  courseStudentIds: Set<string>,
  anderswoStudentIds: Set<string> = new Set(),
): AttendanceEntry[] {
  const isLateStatus = (s: string) =>
    s === "late" || s === "late_excused" || s === "late_unexcused";

  return attendanceArray
    .filter((entry) => !courseStudentIds.has(entry.student_id))
    .map((entry) => {
      if (anderswoStudentIds.has(entry.student_id)) {
        return {
          ...entry,
          status: "present",
          note: null,
          late_minutes: null,
          arrival_time: null,
        };
      }
      return {
        ...entry,
        status: isLateStatus(entry.status) ? "present" : entry.status,
        note: isLateStatus(entry.status)
          ? "Aus vorheriger Stunde übernommen"
          : entry.note,
        late_minutes: isLateStatus(entry.status) ? null : entry.late_minutes,
        arrival_time: isLateStatus(entry.status) ? null : entry.arrival_time,
      };
    });
}
